import React, { useState } from 'react';
import {
  TextField,
  Button,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import '../../App.css';
import { apiUrlPrefix } from '../../config';
import useStore from './state-files/zustondStoreStates';
import { useNavigate } from "react-router-dom";
import { useMediaQuery, Box } from '@mui/system';
import InstallPWAButton from './Components/PWA';

const LoginWidget = ({ setIsCreatingAccount }) => {
  const [error, setError] = useState(null);
  const queryClient = useQueryClient();
  const setDiscussions = useStore((state) => state.setDiscussions);
  const { setCommunities, greenButtonSX, setIsLoggedIn } = useStore(); // Use zustand store
  const navigate = useNavigate(); // Standard web equivalent of useRouter
  const isMobile = useMediaQuery('(max-width:1300px)');

  const login = async ({ email, password }) => {
    const apiUrl = apiUrlPrefix + 'MongoDb/LoginUser';
    const response = await fetch(apiUrl, {
      method: 'POST',
      credentials: 'include', // Include cookies in request
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Invalid credentials. Please check your email and password.');
      }
      //Only go here if it's not a credential issue
      const responseInformation = await response.json();
      
      if (responseInformation === 403) {
      throw new Error('You are banned, please contact genkidesu12039@outlook.co.nz to appeal');
    }
    throw new Error('Network response was not ok');
  }

  const responseValue = await response.json();
  localStorage.setItem('accessToken', responseValue.userInfo.accessToken);
  localStorage.setItem('refreshToken', responseValue.userInfo.token);
  localStorage.setItem('refreshTokenExpiry', responseValue.userInfo.tokenExpiry);
  localStorage.setItem('userId', responseValue.userInfo.id);
  localStorage.setItem('username', responseValue.userInfo.name);
  localStorage.setItem('profilePicture', responseValue.userInfo.profilePicture);
  localStorage.setItem('roles', responseValue.userInfo.roles);
  return responseValue; // Parse and return the JSON response
};

const loginMutation = useMutation({
  mutationFn: login,
  onSuccess: (data) => {
    if (data && data.userInfo) {
      const { userInfo } = data;

      // Cache user info
      queryClient.setQueryData(['user'], {
        email: userInfo.email,
        username: userInfo.name,
        profilePicture: userInfo.profilePicture,
      });

      // Store the object
      localStorage.setItem('userId', data.userInfo.id);
      localStorage.setItem('username', data.userInfo.name);
      formik.setFieldValue('email', '');
      formik.setFieldValue('password', '');

      navigate('/discussions');
      setIsLoggedIn(true); // Hide the login button
    } else {
      throw new Error('Unexpected response structure');
    }
  },
  onError: (error) => {
    setError(error.message); // Set the error message
  },
});

// Formik configuration
const formik = useFormik({
  initialValues: {
    email: queryClient.getQueryData(['user'])?.email || '', // Initialize with cached email
    password: '',
  },
  validationSchema: Yup.object({
    email: Yup.string().email('Invalid email address').required('Email is required'),
    password: Yup.string().required('Password is required'),
  }),
  onSubmit: (values) => {
    setError(null); // Clear previous errors
    loginMutation.mutate(values); // Trigger the mutation with form values
  },
});

return (
  <Box sx={{
    height: '100%',
    bgcolor: 'white',
    borderRadius: 1,
    boxShadow: 1,
    paddingLeft: 4,
    paddingRight: 4,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  }}>
    <Typography id="loginButton" variant="h5" sx={{ textAlign: 'center' }}
      color="none" style={{ boxShadow: "none" }}>
      Login
    </Typography>

    <form onSubmit={formik.handleSubmit}>
      <TextField
        sx={{ backgroundColor: '#FFFFFF' }}
        autoFocus
        margin="dense"
        label="Email"
        type="email"
        fullWidth
        variant="outlined"
        name="email"
        value={formik.values.email} // Controlled by Formik
        onKeyDown={(e) => {
          // This stops the 'a', 'd', 'h', 'l' from reaching your global shortcuts
          e.stopPropagation();
        }}
        onChange={(e) => {
          // Update Formik state
          formik.handleChange(e);

          // Synchronize with queryClient
          queryClient.setQueryData(['user'], (oldData) => ({
            ...oldData,
            email: e.target.value, // Update email in queryClient
          }));
        }}
        onBlur={formik.handleBlur}
        error={formik.touched.email && Boolean(formik.errors.email)}
        helperText={formik.touched.email && formik.errors.email}
      />
      <TextField
        sx={{ backgroundColor: '#FFFFFF' }}
        margin="dense"
        label="Password"
        type="password"
        fullWidth
        variant="outlined"
        name="password"
        value={formik.values.password} // Controlled by Formik
        onKeyDown={(e) => {
          // This stops the 'a', 'd', 'h', 'l' from reaching your global shortcuts
          e.stopPropagation();
        }}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.password && Boolean(formik.errors.password)}
        helperText={formik.touched.password && formik.errors.password}
      />
      {error && (
        <Typography color="error" variant="body2" style={{ marginTop: '10px' }}>
          {error}
        </Typography>
      )}

      <Box sx={{ textAlign: 'center' }}>
        <Button type="submit" color="primary" sx={[greenButtonSX, { padding: 0.5, width: 0.75, borderRadius: 4, marginBottom: 1, marginTop: 1 }]}> Log in </Button>
        <br />
        <Button onClick={() => setIsCreatingAccount(true)} color="primary" sx={[greenButtonSX, { padding: 0.5, width: 0.75, borderRadius: 4 }]}>
          Create Account
        </Button>
      </Box>
    </form>
    {isMobile ? null : (
      <>
        <Typography variant="h5" sx={{ marginTop: 4 }}>Want an app version instead?</Typography>
        <InstallPWAButton />
      </>)}
  </Box>
);
};

export default LoginWidget;
